import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePortalMenu } from "../../../hooks/usePortalMenu";
import { useChipInput } from "../../../hooks/useChipInput";
import { useMultiSelectSearch } from "../../../hooks/useMultiSelectSearch";
import { useSlackConnection } from "../../../hooks/useSlackConnection";
import { listSlackChannels } from "../../../services/notifications/slackApi";
import { queryKeys } from "../../../services/queryKeys";
import { ChipInput } from "../../molecules/ChipInput";
import { MultiSelectSearch } from "../../molecules/MultiSelectSearch";
import { Dropdown, type DropdownOption } from "../../molecules/Dropdown";
import { SlackConnectionStatus } from "../../molecules/SlackConnectionStatus";
import { RadioCardGroup } from "../../molecules/RadioCardGroup";
import { audienceOptions } from "../DistributionSplitStep/distributionSplitStep.viewModel";
import { AUDIENCE_ICONS } from "../DistributionSplitStep/distributionSplitStep.icons";
import { DISTRIBUTION_AUDIENCE_COPY as AUDIENCE } from "../../../constants/distributionHelpContent";
import { useActiveTenantName } from "../../../hooks/useTenantContext";
import { Checkbox } from "../../atoms/Checkbox";
import { PlatformIcon, FormatIcon } from "./icons";
import ScheduleFields, { type ScheduleDropdownKey } from "./ScheduleFields";
import { AlertConditionsSection } from "../AlertConditionsSection";
import {
  TEAMS_OPTIONS,
  EMAIL_REGEX,
} from "./destinationOptions";
import { DELIVER_COPY } from "./deliverStory.copy";
import { isThemedDeck } from "./deliveryTheme";
import { ThemeError } from "../ExportStoryModal/ExportThemePreview/ExportThemePreview.styles";
import type { PresentationThemeOption } from "../../../services/presentationThemes/presentationThemes.types";
import type {
  DeliveryArtifact,
  DeliveryFormat,
  DestinationOption,
  AlertConditionConfig,
  AlertConditionMatchMode,
  Platform,
  ScheduleConfig,
  SendNowFormState,
  Weekday,
  WhenToSend,
} from "./types";
import {
  CONDITION_COMPARATOR_DROPDOWN_OPTIONS,
  CONDITION_CURRENT_METRIC_TEXT,
  CONDITION_METRIC_DROPDOWN_OPTIONS,
} from "./conditionOptions";
import type { ScheduleErrors } from "../../../utils/scheduleValidation";
import type { OpenDropdownKey } from "../../../hooks/useDeliverStory";
import {
  FormCard,
  FormScroll,
  Field,
  SplitFieldRow,
  Label,
  SegmentedRow,
  SegmentButton,
  MessageTextarea,
  CharCount,
  ToggleRow,
  Note,
  ToggleLabel,
  ThemeSwatch,
} from "./SendNowForm.styles";

const MESSAGE_MAX_LENGTH = 300;

const PLATFORMS: { value: Platform; label: string }[] = [
  { value: "slack", label: "Slack" },
  { value: "email", label: "Email" },
  { value: "teams", label: "Teams" },
];

const WHEN_OPTIONS: { value: WhenToSend; label: string }[] = [
  { value: "send-now", label: DELIVER_COPY.whenSendNow },
  { value: "schedule", label: DELIVER_COPY.whenSchedule },
  { value: "alert-on-condition", label: DELIVER_COPY.whenKpi },
];

const ARTIFACT_OPTIONS: { value: DeliveryArtifact; label: string }[] = [
  { value: "story", label: "Story" },
  { value: "memo", label: "Memo" },
  { value: "presentation", label: "Presentation" },
];

const FORMAT_OPTIONS: { value: DeliveryFormat; label: string }[] = [
  { value: "presentation", label: "Presentation" },
  { value: "pdf", label: "PDF" },
  { value: "image-set", label: "Image set" },
  { value: "video", label: "Video" },
];

const isValidEmail = (v: string) => EMAIL_REGEX.test(v);

export interface SendNowFormProps {
  value: SendNowFormState;
  openDropdown: OpenDropdownKey;
  scheduleErrors: ScheduleErrors;
  scheduleTouched: boolean;
  maxAlertConditions: number;
  /** Real metric options from the alert-metrics API. Optional — falls
   * back to the static placeholder list so existing renders/tests that
   * don't pass it keep working unchanged. */
  metricOptions?: DropdownOption<string>[];
  currentMetricTextByMetric?: Record<string, string>;
  onChange: (patch: Partial<SendNowFormState>) => void;
  onAddDestination: (dest: string) => void;
  onRemoveDestination: (dest: string) => void;
  onReplaceDestinations: (dests: string[]) => void;
  onScheduleChange: (patch: Partial<ScheduleConfig>) => void;
  onToggleWeekday: (day: Weekday) => void;
  onAddCondition: () => void;
  onUpdateCondition: (
    id: string,
    patch: Partial<Omit<AlertConditionConfig, "id">>,
  ) => void;
  onRemoveCondition: (id: string) => void;
  onConditionMatchModeChange: (mode: AlertConditionMatchMode) => void;
  onOpenDropdown: (key: Exclude<OpenDropdownKey, null>) => void;
  onCloseDropdown: () => void;
  /** Presentation themes (the same list the Export modal offers). */
  themeOptions?: PresentationThemeOption[];
  /** The theme shown and sent — the pick, else the default (resolveDeliveryTheme). */
  themeValue?: string;
  isThemesLoading?: boolean;
  /** Backend's reason when the theme list couldn't load. */
  themesErrorMessage?: string | null;
  /** Shows the inside/outside choice: only when the send is saved as a distribution. */
  showAudience?: boolean;
}

const SendNowForm: React.FC<SendNowFormProps> = ({
  value,
  openDropdown,
  scheduleErrors,
  scheduleTouched,
  maxAlertConditions,
  metricOptions = CONDITION_METRIC_DROPDOWN_OPTIONS,
  currentMetricTextByMetric = CONDITION_CURRENT_METRIC_TEXT,
  onChange,
  onAddDestination,
  onRemoveDestination,
  onReplaceDestinations,
  onScheduleChange,
  onToggleWeekday,
  onAddCondition,
  onUpdateCondition,
  onRemoveCondition,
  onConditionMatchModeChange,
  onOpenDropdown,
  onCloseDropdown,
  themeOptions = [],
  themeValue,
  isThemesLoading = false,
  themesErrorMessage = null,
  showAudience = false,
}) => {
  const organisation = useActiveTenantName();
  const themedDeck = isThemedDeck(value);
  const isEmail = value.platform === "email";
  const isSlack = value.platform === "slack";

  // Disconnect/switch-workspace intentionally NOT exposed here — that
  // capability belongs in a future dedicated settings/integrations page,
  // not inline in the Share/Deliver Story flow. `useSlackConnection`
  // still exposes `disconnectSlack` for that future page to use.
  const {
    isConnected: isSlackConnected,
    isStatusLoading: isSlackStatusLoading,
    connecting: isSlackConnecting,
    connectError: slackConnectError,
    connectSlack,
  } = useSlackConnection();

  // Real channels replace the old SLACK_OPTIONS mock — only fetched once
  // Slack is actually connected (the endpoint 401/errors otherwise).
  const { data: slackChannelsData } = useQuery({
    queryKey: queryKeys.notifications.slackChannels(),
    queryFn: listSlackChannels,
    enabled: isSlack && isSlackConnected,
  });

  // `value` is the real Slack channel id (e.g. "C0123456"), not the display
  // name — this is what actually gets sent to POST /channels/slack/messages,
  // since Slack's chat.postMessage/files APIs address channels by id.
  const slackDestinationOptions: DestinationOption[] = useMemo(
    () =>
      (slackChannelsData?.channels ?? []).map((c) => ({
        value: c.id,
        label: `#${c.name}`,
        kind: "channel" as const,
      })),
    [slackChannelsData],
  );

  const destinationOptions: DestinationOption[] = isSlack
    ? slackDestinationOptions
    : TEAMS_OPTIONS;
  const destinationPlaceholder = isSlack
    ? "Search channels or people"
    : "Search teams or people";

  const chipInput = useChipInput({
    values: isEmail ? value.destinations : [],
    onAdd: onAddDestination,
    onRemove: onRemoveDestination,
    validate: isValidEmail,
  });

  const multiSelect = useMultiSelectSearch<DestinationOption>({
    value: isEmail ? [] : value.destinations,
    options: destinationOptions,
    onChange: onReplaceDestinations,
  });

  const multiSelectPortal = usePortalMenu<HTMLButtonElement, HTMLDivElement>({
    open: multiSelect.open,
    onOutsideClick: multiSelect.close,
  });

  const formatPortal = usePortalMenu<HTMLButtonElement, HTMLUListElement>({
    open: openDropdown === "format",
    onOutsideClick: onCloseDropdown,
  });

  const artifactPortal = usePortalMenu<HTMLButtonElement, HTMLUListElement>({
    open: openDropdown === "artifact",
    onOutsideClick: onCloseDropdown,
  });

  const themePortal = usePortalMenu<HTMLButtonElement, HTMLUListElement>({
    open: openDropdown === "theme",
    onOutsideClick: onCloseDropdown,
  });

  const themeDropdownOptions: DropdownOption<string>[] = useMemo(
    () =>
      themeOptions.map((t) => ({
        value: t.key,
        label: t.label,
        icon: t.swatch ? <ThemeSwatch $color={t.swatch} aria-hidden /> : undefined,
      })),
    [themeOptions],
  );

  const artifactOptions: DropdownOption<DeliveryArtifact>[] = useMemo(
    () => ARTIFACT_OPTIONS,
    [],
  );

  const formatOptions: DropdownOption<DeliveryFormat>[] = useMemo(
    () =>
      FORMAT_OPTIONS.map((o) => ({
        value: o.value,
        label: o.label,
        icon: <FormatIcon format={o.value} />,
      })),
    [],
  );

  const scheduleDropdown: ScheduleDropdownKey | null =
    openDropdown === "scheduleUnit" ||
    openDropdown === "scheduleHour" ||
    openDropdown === "scheduleTimezone"
      ? openDropdown
      : null;

  const renderDestinationField = () => {
    if (isEmail) {
      return (
        <ChipInput
          type="email"
          values={value.destinations}
          draft={chipInput.draft}
          error={chipInput.error}
          onRemove={onRemoveDestination}
          onChange={chipInput.handleChange}
          onKeyDown={chipInput.handleKeyDown}
          onBlur={chipInput.handleBlur}
          placeholder="Enter email address"
          errorText="Enter a valid email address"
        />
      );
    }
    if (isSlack && !isSlackConnected) {
      return (
        <SlackConnectionStatus
          isConnected={isSlackConnected}
          isStatusLoading={isSlackStatusLoading}
          connecting={isSlackConnecting}
          connectError={slackConnectError}
          onConnect={() => connectSlack()}
        />
      );
    }
    return (
      <MultiSelectSearch<DestinationOption>
        value={value.destinations}
        options={destinationOptions}
        filtered={multiSelect.filtered}
        open={multiSelect.open}
        query={multiSelect.query}
        onToggleOpen={multiSelect.toggleOpen}
        onQueryChange={multiSelect.setQuery}
        onToggleOption={multiSelect.toggleOption}
        triggerRef={multiSelectPortal.triggerRef}
        menuRef={multiSelectPortal.menuRef}
        menuRect={multiSelectPortal.rect}
        placeholder={destinationPlaceholder}
        searchPlaceholder={destinationPlaceholder}
      />
    );
  };

  return (
    <FormCard>
      <FormScroll>
        <Field>
          <Label>{DELIVER_COPY.sendTo}</Label>
          <SegmentedRow role="radiogroup" aria-label={DELIVER_COPY.sendTo}>
            {PLATFORMS.map((p) => {
              const checked = value.platform === p.value;
              return (
                <SegmentButton
                  key={p.value}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  $active={checked}
                  onClick={() => onChange({ platform: p.value })}
                >
                  <PlatformIcon platform={p.value} size={18} />
                  {p.label}
                </SegmentButton>
              );
            })}
          </SegmentedRow>
        </Field>

        <Field>
          <Label>{DELIVER_COPY.destination}</Label>
          {isSlack && isSlackConnected && (
            <SlackConnectionStatus
              isConnected={isSlackConnected}
              isStatusLoading={isSlackStatusLoading}
              connecting={isSlackConnecting}
              connectError={slackConnectError}
              onConnect={() => connectSlack()}
            />
          )}
          {renderDestinationField()}
          {/* Moved to the Preview panel (below the attachment image, where
              there's white space) per follow-up feedback — see
              DeliveryPreview.tsx's SlackPreview. */}
        </Field>

        <SplitFieldRow>
          <Field>
            <Label>{DELIVER_COPY.whatToSend}</Label>
            <Dropdown
              value={value.artifact}
              options={artifactOptions}
              open={openDropdown === "artifact"}
              triggerRef={artifactPortal.triggerRef}
              menuRef={artifactPortal.menuRef}
              menuRect={artifactPortal.rect}
              size="md"
              compact
              ariaLabel={DELIVER_COPY.whatToSend}
              onToggle={() => onOpenDropdown("artifact")}
              onSelect={(artifact) => onChange({ artifact })}
            />
          </Field>

          <Field>
            <Label>{DELIVER_COPY.format}</Label>
            <Dropdown
              value={value.format}
              options={formatOptions}
              open={openDropdown === "format"}
              triggerRef={formatPortal.triggerRef}
              menuRef={formatPortal.menuRef}
              menuRect={formatPortal.rect}
              size="md"
              compact
              ariaLabel={DELIVER_COPY.format}
              onToggle={() => onOpenDropdown("format")}
              onSelect={(fmt) => onChange({ format: fmt })}
            />
          </Field>
        </SplitFieldRow>

          <Field>
            <Label>{DELIVER_COPY.theme}</Label>
            <Dropdown
              value={themedDeck ? (themeValue ?? null) : null}
              options={themeDropdownOptions}
              open={openDropdown === "theme"}
              triggerRef={themePortal.triggerRef}
              menuRef={themePortal.menuRef}
              menuRect={themePortal.rect}
              size="md"
              compact
              disabled={!themedDeck || isThemesLoading || themeDropdownOptions.length === 0}
              placeholder={
                !themedDeck
                  ? DELIVER_COPY.themeNotApplicable
                  : isThemesLoading
                    ? "Loading themes…"
                    : "No themes available"
              }
              ariaLabel={DELIVER_COPY.theme}
              onToggle={() => onOpenDropdown("theme")}
              onSelect={(theme) => onChange({ theme })}
            />
            {themedDeck && themesErrorMessage && <ThemeError role="alert">{themesErrorMessage}</ThemeError>}
          </Field>

        <Field>
          <Label>{DELIVER_COPY.whenLabel}</Label>
          <SegmentedRow role="tablist">
            {WHEN_OPTIONS.map((o) => {
              const active = value.whenToSend === o.value;
              return (
                <SegmentButton
                  key={o.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  $active={active}
                  onClick={() => onChange({ whenToSend: o.value })}
                >
                  {o.label}
                </SegmentButton>
              );
            })}
          </SegmentedRow>
        </Field>

        {value.whenToSend === "alert-on-condition" && (
          <AlertConditionsSection
            conditions={value.alertConditions}
            maxConditions={maxAlertConditions}
            matchMode={value.conditionMatchMode}
            metricOptions={metricOptions}
            comparatorOptions={CONDITION_COMPARATOR_DROPDOWN_OPTIONS}
            currentMetricTextByMetric={currentMetricTextByMetric}
            onAdd={onAddCondition}
            onUpdate={onUpdateCondition}
            onRemove={onRemoveCondition}
            onMatchModeChange={onConditionMatchModeChange}
          />
        )}

        {value.whenToSend === "schedule" && (
          <ScheduleFields
            value={value.schedule}
            errors={scheduleErrors}
            showErrors={scheduleTouched}
            openDropdown={scheduleDropdown}
            onChange={onScheduleChange}
            onToggleWeekday={onToggleWeekday}
            onOpenDropdown={onOpenDropdown}
            onCloseDropdown={onCloseDropdown}
          />
        )}
        {value.whenToSend === "schedule" && <Note>{DELIVER_COPY.refreshNote}</Note>}

        {value.whenToSend === "alert-on-condition" && (
          <ToggleRow>
            <Checkbox
              checked={value.includeAttachment}
              onChange={(includeAttachment) => onChange({ includeAttachment })}
              ariaLabel={DELIVER_COPY.includeAttachment}
            />
            <ToggleLabel>{DELIVER_COPY.includeAttachment}</ToggleLabel>
          </ToggleRow>
        )}

        {showAudience && (
        <RadioCardGroup
          name="deliver-story-audience"
          legend={AUDIENCE.legend}
          hint={AUDIENCE.hint}
          options={audienceOptions(organisation, AUDIENCE_ICONS)}
          value={value.audience}
          onChange={(audience) => onChange({ audience: audience === "inside" ? "inside" : "outside" })}
          columns={2}
          appearance="plain"
        />
        )}

        <Field>
          <Label>{DELIVER_COPY.message}</Label>
          <MessageTextarea
            value={value.message}
            placeholder={DELIVER_COPY.messagePlaceholder}
            maxLength={MESSAGE_MAX_LENGTH}
            onChange={(e) =>
              onChange({ message: e.target.value.slice(0, MESSAGE_MAX_LENGTH) })
            }
          />
          <CharCount $overLimit={value.message.length >= MESSAGE_MAX_LENGTH}>
            {value.message.length}/{MESSAGE_MAX_LENGTH}
          </CharCount>
        </Field>
      </FormScroll>
    </FormCard>
  );
};

export default SendNowForm;
